import {useState} from 'react';
import {shallow} from 'zustand/shallow';
import {useIntl} from 'react-intl';
import {useRouter} from 'next/router';
import {Button, ButtonProps, useDisclosure, useToast} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useMeQuery, useRegionSettings} from '@eksab/hooks';
import {VerifyNumberToJoin} from '@eksab/components';
import {AuthModal} from '@eksab/features/auth/modals/AuthModal';

import {MultiEntryCompetition, useSubmitLineupMutation} from './useSubmitLineupMutation';
import {useLineupStore} from './useLineupStore';
import {lineupCache} from './lineupCache';
import {useCompetitionQuery} from '../hooks';
import {JoinCompetitionModal, SuccessfulSubmissionModal} from '../components/modals';
import {ReferralModal} from '../components/modals/ReferralModal';

export function SubmitLineupButton(props: ButtonProps) {
  const format = useIntl().formatNumber;
  const slug = useRouter().query.slug as string;
  const toast = useToast();
  const [similarCompetitions, setSimilarCompetitions] = useState<MultiEntryCompetition[]>([]);
  const {players, substitutes, overallCost, formation, numberOfPlayers} = useLineupStore(
    (state) => ({
      players: state.players,
      substitutes: state.substitutes,
      overallCost: state.overallCost,
      formation: state.formation,
      numberOfPlayers: state.numberOfPlayers,
    }),
    shallow,
  );
  const verifyNumberDisclosure = useDisclosure();
  const {onClose: _onAuthModalClose, ...restAuthDisclosure} = useDisclosure();
  const {onClose: _onJoinCompetitionModalClose, ...restJoinDisclosure} = useDisclosure();
  const successfulSubmissionDisclosure = useDisclosure();

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const {data: {id, state, entry_fee: fees = 0, budget = 0, joined, participants} = {}, refetch} = useCompetitionQuery(
    slug,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
  const submitLineupMutation = useSubmitLineupMutation(slug);

  const isCompetitionJoinable = state === 'upcoming';
  const isJoined = async () => (await refetch()).data?.joined;

  const submitLineup = () =>
    submitLineupMutation.mutate(
      {players, substitutes, formation},
      {
        onSuccess: ({similar_competitions}) => {
          lineupCache.removeLineup(id!);

          if (!similar_competitions?.length)
            return toast({status: 'success', title: t('fantasy.lineup.submit.success')});

          setSimilarCompetitions(similar_competitions);
          successfulSubmissionDisclosure.onOpen();
        },
        onError: (error) => {
          toast({status: 'error', title: error.response?.data?.message ?? t('app.errors.try-again')});
        },
      },
    );

  const onAuthModalClose = async (me: Me | undefined) => {
    if (!me) return _onAuthModalClose();

    if (await isJoined()) submitLineup();
    else joinDisclosure.onOpen();

    _onAuthModalClose();
  };
  const authDisclosure = {onClose: onAuthModalClose, ...restAuthDisclosure};

  const onJoinCompetitionModalClose = async () => {
    if (await isJoined()) submitLineup();

    _onJoinCompetitionModalClose();
  };
  const joinDisclosure = {onClose: onJoinCompetitionModalClose, ...restJoinDisclosure};
  const {isOpen: isReferralModalOpen, onOpen: onReferralModalOpen, onClose: onReferralModalClose} = useDisclosure();
  const {data: regionSettings} = useRegionSettings();

  const onSubmitLineup = () => {
    if (!isCompetitionJoinable) return toast({title: t('competition.join.warning.not-joinable'), status: 'error'});

    if (overallCost > +budget)
      return toast({
        title: t('fantasy.lineup.submit.warning.over-budget', {
          overAmount: t('fantasy.lineup.price-unit', {
            amount: format(overallCost - +budget),
          }),
        }),
        status: 'warning',
      });

    const _players = players.flat().concat(substitutes);

    const hasInvalidPlayer = _players.some(
      (player) => player?.selection_status && player.selection_status.status !== 'valid',
    );
    if (hasInvalidPlayer)
      return toast({
        title: t('fantasy.lineup.submit.warning.invalid-player'),
        status: 'warning',
      });

    const hasCaptain = _players.some((player) => !!player?.is_captain),
      hasViceCaptain = _players.some((player) => !!player?.is_vice_captain);
    if (!hasCaptain || !hasViceCaptain)
      return toast({
        title: t('fantasy.lineup.submit.warning.no-leader', {
          leader: t(`fantasy.lineup.${!hasCaptain ? 'captain' : 'vice-captain'}`),
        }),
        status: 'warning',
      });

    if (!me || !joined) {
      lineupCache.setLineup(id!, {numberOfPlayers, formation, players, substitutes, overallCost});
      toast({title: t('fantasy.lineup.save.success')});
    }

    mixpanel?.track('Button Clicked', {button: 'competition.save_lineup'});

    if (!me) return authDisclosure.onOpen();

    if (regionSettings?.settings.phone_number_verification_enabled && !me.phone_verified)
      return verifyNumberDisclosure.onOpen();

    if (!joined) {
      if (
        (me?.coins ?? 0) < fees &&
        !regionSettings?.settings?.coin_packages_enabled &&
        !regionSettings?.settings?.badge_packages_enabled
      )
        return onReferralModalOpen();
      return joinDisclosure.onOpen();
    }

    submitLineup();
  };

  return (
    <>
      <Button
        {...props}
        onClick={onSubmitLineup}
        isLoading={submitLineupMutation.isLoading}
        disabled={submitLineupMutation.isLoading || numberOfPlayers !== 15}
      >
        {t(`fantasy.lineup.${joined ? 'submit' : 'save'}`)}
      </Button>

      <AuthModal {...authDisclosure} />
      <VerifyNumberToJoin {...verifyNumberDisclosure} />
      <SuccessfulSubmissionModal {...successfulSubmissionDisclosure} competitions={similarCompetitions} />
      <JoinCompetitionModal
        competitionType="fantasy"
        fees={fees}
        isCapped={!!participants?.max}
        canJoin={(me?.coins ?? 0) >= fees}
        {...joinDisclosure}
      />
      <ReferralModal isOpen={isReferralModalOpen} onClose={onReferralModalClose} />
    </>
  );
}
